import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send, MapPin, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "../services/api";

const roleOptions = [
  "General Enquiry",
  "Renter — Search Brief",
  "Landlord — List Property",
  "Property Partner / Agency",
  "FIND LET Move",
  "Press & Media",
];

export const Contact = () => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    role: roleOptions[0],
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete every field before sending.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact-messages", form);
      setSent(true);
      toast.success("Message received. Our concierge desk will reply within one business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F9FBFF] text-[#091F5C]">
      {/* HERO STRIP */}
      <section className="bg-white border-b border-[#E5EBF5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8 space-y-6">
            <span className="eyebrow">Concierge Desk · London</span>
            <h1 className="display-hero text-4xl sm:text-5xl lg:text-6xl">
              A quiet line to our concierge desk.
            </h1>
            <p className="text-base text-[#4A5B78] font-light leading-relaxed max-w-2xl">
              For search briefs, listings, partnerships, or press — a single, considered inbox. Our London operators triage every message and reply within one business day.
            </p>
          </div>
          <div className="lg:col-span-4 space-y-4">
            <a
              href="mailto:concierge@findlet.uk"
              data-testid="contact-hero-email-link"
              className="flex items-start gap-3 p-5 bg-[#F9FBFF] border border-[#E5EBF5] hover:border-[#091F5C] transition-colors"
            >
              <Mail className="w-5 h-5 text-[#334DAF] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold">Email</div>
                <div className="text-sm font-heading font-semibold text-[#091F5C] mt-1">concierge@findlet.uk</div>
                <div className="text-xs text-[#4A5B78] mt-1 font-light">Preferred for all enquiries.</div>
              </div>
            </a>
            <div className="flex items-start gap-3 p-5 bg-[#F9FBFF] border border-[#E5EBF5]">
              <MapPin className="w-5 h-5 text-[#334DAF] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold">Desk</div>
                <div className="text-sm font-heading font-semibold text-[#091F5C] mt-1">Mayfair & City of London</div>
                <div className="text-xs text-[#4A5B78] mt-1 font-light">By appointment only.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-5 bg-[#F9FBFF] border border-[#E5EBF5]">
              <Clock className="w-5 h-5 text-[#334DAF] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold">Hours</div>
                <div className="text-sm font-heading font-semibold text-[#091F5C] mt-1">Mon – Sat · 08:30–19:30 GMT</div>
                <div className="text-xs text-[#4A5B78] mt-1 font-light">Sunday reserved for viewings only.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORM SECTION */}
      <section className="py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left copy */}
          <aside className="lg:col-span-5 space-y-8">
            <div>
              <span className="eyebrow">Send a message</span>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-[#091F5C] mt-3 leading-tight">
                Tell us how we can help.
              </h2>
              <p className="text-sm text-[#4A5B78] mt-4 font-light leading-relaxed">
                Every message lands in our concierge triage queue. If you are a renter with a live search brief or a landlord with inventory, submitting via the wizards linked below will fast-track your enquiry.
              </p>
            </div>

            <div className="space-y-3 border-t border-[#E5EBF5] pt-6">
              <Link to="/find" data-testid="contact-link-find" className="flex items-center justify-between group py-2 border-b border-[#E5EBF5]">
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold">Renters</div>
                  <div className="text-sm font-heading font-semibold text-[#091F5C] mt-1">Submit a search brief</div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#091F5C] group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/let" data-testid="contact-link-let" className="flex items-center justify-between group py-2 border-b border-[#E5EBF5]">
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold">Landlords</div>
                  <div className="text-sm font-heading font-semibold text-[#091F5C] mt-1">List a property</div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#091F5C] group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/partners" data-testid="contact-link-partners" className="flex items-center justify-between group py-2 border-b border-[#E5EBF5]">
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold">Agencies</div>
                  <div className="text-sm font-heading font-semibold text-[#091F5C] mt-1">Partner with FIND LET</div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#091F5C] group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/move" data-testid="contact-link-move" className="flex items-center justify-between group py-2 border-b border-[#E5EBF5]">
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#334DAF] font-heading font-semibold">FIND LET Move</div>
                  <div className="text-sm font-heading font-semibold text-[#091F5C] mt-1">Book relocation services</div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#091F5C] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </aside>

          {/* Right form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#E5EBF5] p-8 lg:p-10">
              {sent ? (
                <div data-testid="contact-form-success" className="text-center py-8 space-y-4">
                  <div className="mx-auto w-12 h-12 rounded-full bg-[#E8F2FE] border border-[#D0E4FE] flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-[#091F5C]" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-[#091F5C]">Message received.</h3>
                  <p className="text-sm text-[#4A5B78] font-light max-w-md mx-auto leading-relaxed">
                    A concierge operator will read your note and reply from <a href="mailto:concierge@findlet.uk" className="editorial-link">concierge@findlet.uk</a> within one business day.
                  </p>
                  <button
                    onClick={() => { setSent(false); setForm({ full_name: "", email: "", role: roleOptions[0], subject: "", message: "" }); }}
                    data-testid="contact-form-reset"
                    className="btn-secondary inline-flex items-center gap-2 mt-4"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] tracking-[0.18em] uppercase text-[#4A5B78] font-heading font-semibold mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={form.full_name}
                        onChange={set("full_name")}
                        placeholder="e.g. Eleanor Vane"
                        data-testid="contact-input-name"
                        className="w-full px-4 py-3 bg-[#F9FBFF] border border-[#E5EBF5] focus:border-[#091F5C] focus:ring-1 focus:ring-[#091F5C] outline-none text-sm text-[#091F5C] placeholder:text-[#7096D1] font-body transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] tracking-[0.18em] uppercase text-[#4A5B78] font-heading font-semibold mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@example.com"
                        data-testid="contact-input-email"
                        className="w-full px-4 py-3 bg-[#F9FBFF] border border-[#E5EBF5] focus:border-[#091F5C] focus:ring-1 focus:ring-[#091F5C] outline-none text-sm text-[#091F5C] placeholder:text-[#7096D1] font-body transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] tracking-[0.18em] uppercase text-[#4A5B78] font-heading font-semibold mb-2">
                      I am contacting FIND LET as
                    </label>
                    <select
                      value={form.role}
                      onChange={set("role")}
                      data-testid="contact-select-role"
                      className="w-full px-4 py-3 bg-[#F9FBFF] border border-[#E5EBF5] focus:border-[#091F5C] focus:ring-1 focus:ring-[#091F5C] outline-none text-sm text-[#091F5C] font-body transition-colors"
                    >
                      {roleOptions.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] tracking-[0.18em] uppercase text-[#4A5B78] font-heading font-semibold mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={set("subject")}
                      placeholder="A short summary of your enquiry"
                      data-testid="contact-input-subject"
                      className="w-full px-4 py-3 bg-[#F9FBFF] border border-[#E5EBF5] focus:border-[#091F5C] focus:ring-1 focus:ring-[#091F5C] outline-none text-sm text-[#091F5C] placeholder:text-[#7096D1] font-body transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] tracking-[0.18em] uppercase text-[#4A5B78] font-heading font-semibold mb-2">
                      Message
                    </label>
                    <textarea
                      value={form.message}
                      onChange={set("message")}
                      rows={6}
                      maxLength={2000}
                      placeholder="Tell our concierge desk what you are looking for, what timelines matter, and any specifics we should know."
                      data-testid="contact-input-message"
                      className="w-full px-4 py-3 bg-[#F9FBFF] border border-[#E5EBF5] focus:border-[#091F5C] focus:ring-1 focus:ring-[#091F5C] outline-none text-sm text-[#091F5C] placeholder:text-[#7096D1] font-body transition-colors resize-none"
                    />
                    <div className="text-[10px] text-[#7096D1] mt-1 text-right">{form.message.length} / 2000</div>
                  </div>

                  <div className="pt-2 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-[11px] text-[#4A5B78] font-light max-w-sm">
                      By sending, you agree that FIND LET may contact you regarding your enquiry.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-btn"
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                      <span>{submitting ? "Sending…" : "Send message"}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
